import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router } from "@angular/router";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AuthService } from "../auth.service";

/**
 * JIRA-EMFDU-001 T-22 - route guards for /private/account/:accountId/settings/*.
 *
 * Before this, none of the Settings child routes had a canActivate at all. The only thing keeping
 * non-administrators out was the hidden gear icon in the header, and hiding a menu entry is not
 * access control - the pages were reachable by typing the URL. That mattered little while nobody
 * was given the gear; T-20 now shows it to EMFD+U users, so /settings/members becomes the obvious
 * next URL to try.
 *
 * Both guards read principal.membership directly rather than going through UserStateService, whose
 * initialize() is asynchronous - a guard can run before it resolves, and isAccountAdmin() would
 * then return undefined and lock out a real administrator.
 */

/** Walks up the route tree for :accountId - it is declared on the parent 'settings' route. */
function resolveAccountId(route: ActivatedRouteSnapshot): number | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
        const raw = current.paramMap.get("accountId");
        if (raw !== null && raw !== undefined && raw !== "") {
            const parsed = +raw;
            return isNaN(parsed) ? null : parsed;
        }
        current = current.parent;
    }
    return null;
}

/** Superadmin, or an administrator of this specific account. Mirrors AuthService.can('update'). */
function isAccountAdministrator(auth: AuthService, accountId: number | null): boolean {
    const principal = auth.getPrincipal();
    if (!principal) {
        return false;
    }
    if (principal.isSuperadmin) {
        return true;
    }
    if (accountId === null) {
        return false;
    }
    const member = principal.membership.find((m) => m.accountID === accountId);
    return !!member && !!member.isAccountAdmin;
}

/**
 * Administrator-only Settings pages: Fields, Members, Global Overlays.
 *
 * EMFD+U users must not reach these - AC-9 keeps user, role and permission management, and overlay
 * configuration, administrator-only.
 */
@Injectable({ providedIn: "root" })
export class AccountSettingsAdminGuard implements CanActivate {
    constructor(
        private readonly authService: AuthService,
        private readonly router: Router
    ) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        // Refresh an expired access token before deciding - logging out on expiry alone sent idle
        // users to the login page.
        return this.authService.ensureAuthenticated().pipe(
            map((authenticated) => {
                if (!authenticated) {
                    return false;
                }
                if (isAccountAdministrator(this.authService, resolveAccountId(route))) {
                    return true;
                }
                this.router.navigate(["/"]);
                return false;
            })
        );
    }
}

/**
 * The Groups settings page: administrators, plus EMFD+U users who hold the upload permission on
 * this account.
 *
 * The permission test mirrors the backend's PrincipalUser.canUpload exactly - superadmin, or
 * account admin, or the EMFD/WRITE bit - so the two cannot drift apart. The page itself remains
 * scoped server-side: the group listing returns only the caller's own hierarchy, and create is
 * restricted to groups they already hold.
 */
@Injectable({ providedIn: "root" })
export class AccountSettingsGroupsGuard implements CanActivate {
    constructor(
        private readonly authService: AuthService,
        private readonly router: Router
    ) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        return this.authService.ensureAuthenticated().pipe(
            map((authenticated) => {
                if (!authenticated) {
                    return false;
                }

                const accountId = resolveAccountId(route);
                // canUpload already folds in superadmin and account admin, and is the same predicate the
                // backend enforces - keeping them in one place stops the two drifting apart.
                if (accountId !== null && this.authService.canUpload(accountId)) {
                    return true;
                }

                this.router.navigate(["/"]);
                return false;
            })
        );
    }
}
